/**
 * Turns George Design System debug information on
 * https://designsystem.g-labs.io/main/resources/designers/bookmarklets/#trigger-debug-info
 */
export function triggerDebugInfo(): void {
    // @ts-expect-error secret global GDS method
    // eslint-disable-next-line no-underscore-dangle
    if (typeof window.__gdsDebugInfo === 'function') {
        // @ts-expect-error secret global GDS method
        // eslint-disable-next-line no-underscore-dangle
        window.__gdsDebugInfo();
    }
}

/**
 * Removes/restores all references to stylesheets and all inline styles.
 * Also adds/removes some smart defaults, i.e. adds table borders, highlights visually hidden text,
 * and makes all inline SVGs 16x16 pixels in size.
 * https://designsystem.g-labs.io/main/resources/designers/bookmarklets/#trigger-debug-info#toggle-all-styles
 */
export function toggleAllStyles(): void {
    const DEFAULTS = 'data-nocss';
    let smartDefaults = document.querySelector(`style[${DEFAULTS}]`);
    const elements = document.querySelectorAll('link[rel=stylesheet], style, [style]');
    Array.from(elements).forEach((node) => {
        const actualAttributeName = ['link', 'style'].includes(node.tagName.toLowerCase())
            ? 'media'
            : 'style';
        const backupAttributeName = `${DEFAULTS}-${actualAttributeName}`;
        if (smartDefaults) {
            const backup = node.getAttribute(backupAttributeName);
            if (backup) {
                node.setAttribute(actualAttributeName, backup);
            } else {
                node.removeAttribute(actualAttributeName);
            }
            node.removeAttribute(backupAttributeName);
        } else {
            const actual = node.getAttribute(actualAttributeName);
            if (actual) {
                node.setAttribute(backupAttributeName, actual);
            }
            node.setAttribute(actualAttributeName, 'inactive');
        }
    });
    if (smartDefaults) {
        smartDefaults.parentNode?.removeChild(smartDefaults);
    } else {
        smartDefaults = document.createElement('style');
        smartDefaults.setAttribute(DEFAULTS, elements.length.toString());
        smartDefaults.appendChild(
            document.createTextNode(`
            :root{--sr:#800080;--no-sr:#80008040}
            .g-avatar img,svg{width:16px!important;height:16px!important}
            svg{fill:currentColor}
            table{border:1px outset ButtonFace}
            th,td{padding:4px;border:1px inset ButtonFace}
            .visually-hidden,.sr-only{color:var(--sr)}
            [aria-label],[aria-hidden="true"]{color:var(--no-sr)}
            [aria-label]{border:1px solid var(--sr);border-radius:4px}
            [aria-label]::before{content:attr(aria-label);background-color:var(--sr);color:#fff}
        `),
        );
        document.querySelector('head')?.appendChild(smartDefaults);
    }
}

/**
 * Shows/hides visually hidden text, i.e. text for assistive technology such as screen readers
 * https://designsystem.g-labs.io/main/resources/designers/bookmarklets/#toggle-visually-hidden-text
 */
export function toggleVisuallyHiddenText(): void {
    const title = 'Show visually hidden text';
    const remove = document.querySelector('style[data-title="$1"]'.replace('$1', title));
    if (remove) {
        document.querySelector('head')?.removeChild(remove);
    } else {
        const add = document.createElement('style');
        add.setAttribute('data-title', title);
        const overrides = [
            'position:absolute',
            'top:auto',
            'left:auto',
            'width:auto',
            'height:auto',
            'margin:0',
            'padding:1px',
            'outline-style:dashed',
            'outline-width:1px',
            'background-color:rgba(255,51,153,.05)',
            'color:rgba(255,51,153,.7)',
            'clip:auto',
            '-webkit-clip-path:none',
            'clip-path:none',
        ];
        add.appendChild(
            document.createTextNode(
                `.g-bootstrap.g-bs4 .sr-only{${overrides.concat(['']).join('!important;')}}`,
            ),
        );
        document.querySelector('head')?.appendChild(add);
    }
}

/**
 * Shows/hides touch targets, i.e. adds an outline to links and buttons
 * that have a touch target area larger than the actual element.
 * On focus, the outline style changes, so you can pinpoint which outline belongs to which element.
 * https://designsystem.g-labs.io/main/resources/designers/bookmarklets/#toggle-enlarged-touch-targets
 */
export function toggleEnlargedTouchTargets(): void {
    const title = 'Show enlarged touch targets';
    const remove = document.querySelector('style[data-title="$1"]'.replace('$1', title));
    if (remove) {
        document.querySelector('head')?.removeChild(remove);
    } else {
        const add = document.createElement('style');
        add.setAttribute('data-title', title);
        const makeStyles = (styles: string[], state?: string): string =>
            `.g-bootstrap.g-bs4 :where(a, button)${state || ''}::before{${styles
                .concat([''])
                .join('!important;')}}`;
        add.appendChild(
            document.createTextNode(
                `${makeStyles(['outline:1px dashed hotpink', 'outline-offset:-1px'])}${makeStyles(
                    ['outline-style:dotted'],
                    ':focus',
                )}`,
            ),
        );
        document.querySelector('head')?.appendChild(add);
    }
}

/**
 * Shows/hides the mouse cursor in the current browser window.
 * Use it to prevent cheating when testing keyboard accessibility.
 * https://designsystem.g-labs.io/main/resources/designers/bookmarklets/#toggle-mouse-cursor
 */
export function toggleMouseCursor(): void {
    const title = 'Hide mouse cursor';
    const remove = document.querySelector('style[data-title="$1"]'.replace('$1', title));
    if (remove) {
        document.querySelector('head')?.removeChild(remove);
    } else {
        const add = document.createElement('style');
        add.setAttribute('data-title', title);
        add.appendChild(document.createTextNode('*,*::before,*::after{cursor:none!important}'));
        document.querySelector('head')?.appendChild(add);
    }
}
